import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users, Wifi, WifiOff, Network, Activity, Clock, Zap } from 'lucide-react';
import { dashboardApi } from '../../api/endpoints/dashboard';
import { monitoringApi } from '../../api/endpoints/monitoring';
import { logsApi } from '../../api/endpoints/logs';
import { useSocket } from '../../hooks/useSocket';
import { queryClient } from '../../providers/QueryProvider';
import { StatCard, Card } from '../../components/ui/Card';
import { Badge, StatusBadge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { MetricBar } from '../../components/ui/MetricBar';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatTimeAgo } from '../../utils/formatters';
import type { Client } from '../../types/client';

const healthColors: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
  stale: 'bg-gray-400',
  'no-data': 'bg-gray-300',
};

function computeHealth(c: Client): string {
  if (!c.metrics) return 'no-data';
  const age = c.metrics.collected_at
    ? (Date.now() - new Date(c.metrics.collected_at).getTime()) / 1000
    : 999;
  if (age > 120) return 'stale';
  if (c.metrics.tunnel_status === 'healthy' && c.metrics.ssh_connection === 'up') return 'healthy';
  if (c.metrics.tunnel_status === 'degraded') return 'degraded';
  if (c.metrics.ssh_connection === 'down' || c.metrics.tunnel_status === 'down') return 'down';
  return 'degraded';
}

const eventBadgeVariants: Record<string, 'green' | 'red' | 'yellow' | 'blue' | 'gray' | 'purple'> = {
  connect: 'green',
  disconnect: 'red',
  auth_success: 'blue',
  auth_fail: 'yellow',
  error: 'red',
  tunnel_open: 'green',
  tunnel_close: 'yellow',
};

export function DashboardPage() {
  // Stats query -- backend returns { success, stats: { total, connected, ... } }
  const { data: statsResp, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.stats(),
    refetchInterval: 30_000,
  });

  // Monitoring overview for health breakdown + connected clients list
  const { data: monitoringData } = useQuery({
    queryKey: ['dashboard-monitoring'],
    queryFn: () => monitoringApi.overview({ limit: 200 }),
    refetchInterval: 30_000,
  });

  // Recent logs
  const { data: logsData } = useQuery({
    queryKey: ['dashboard-logs'],
    queryFn: () => logsApi.list({ per_page: 10 }),
    refetchInterval: 30_000,
  });

  // Real-time stats updates via Socket.IO
  useSocket('stats', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-monitoring'] });
  }, []));

  useSocket('statusUpdate', useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-monitoring'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-logs'] });
  }, []));

  // Unwrap stats from the nested response
  const stats = statsResp?.stats || { total: 0, connected: 0, disconnected: 0, registered: 0, activePorts: 0 };
  const allClients = monitoringData?.clients || [];
  const summary = monitoringData?.summary || {
    total: 0, healthy: 0, degraded: 0, down: 0, noMetrics: 0,
    avgCpu: 0, avgMemory: 0, avgDisk: 0,
  };
  const logs = logsData?.logs || [];

  // Connected clients (top 10)
  const connectedClients = allClients
    .filter((c: Client) => c.status === 'connected')
    .slice(0, 10);

  // Compute average latency across clients with metrics
  const clientsWithLatency = allClients.filter(
    (c: Client) => c.metrics?.latency_ms != null
  );
  const avgLatency = clientsWithLatency.length > 0
    ? Math.round(
        clientsWithLatency.reduce((sum: number, c: Client) => sum + (c.metrics?.latency_ms || 0), 0)
        / clientsWithLatency.length
      )
    : null;

  if (statsLoading && !statsResp) return <PageSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Overview of your tunnel infrastructure</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Clients" value={stats.total} color="purple" icon={<Users size={18} />} />
        <StatCard label="Connected" value={stats.connected} color="green" icon={<Wifi size={18} />} />
        <StatCard label="Disconnected" value={stats.disconnected} color="red" icon={<WifiOff size={18} />} />
        <StatCard label="Active Ports" value={stats.activePorts} color="blue" icon={<Network size={18} />} />
      </div>

      {/* Health Overview */}
      <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Tunnel Health Overview</h2>
          <Link
            to="/monitoring"
            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
          >
            Full Monitoring Dashboard
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="flex items-center gap-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Healthy</p>
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">{summary.healthy}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <span className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Degraded</p>
              <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">{summary.degraded}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Down</p>
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">{summary.down}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <span className="w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">No Agent</p>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{summary.noMetrics}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Zap size={16} className="text-blue-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Avg Latency</p>
              <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                {avgLatency != null ? `${avgLatency}ms` : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Health bar visualization */}
        {summary.total > 0 && (
          <div className="mt-4">
            <div className="flex rounded-full overflow-hidden h-2">
              {summary.healthy > 0 && (
                <div
                  className="bg-green-500"
                  style={{ width: `${(summary.healthy / summary.total) * 100}%` }}
                  title={`Healthy: ${summary.healthy}`}
                />
              )}
              {summary.degraded > 0 && (
                <div
                  className="bg-yellow-500"
                  style={{ width: `${(summary.degraded / summary.total) * 100}%` }}
                  title={`Degraded: ${summary.degraded}`}
                />
              )}
              {summary.down > 0 && (
                <div
                  className="bg-red-500"
                  style={{ width: `${(summary.down / summary.total) * 100}%` }}
                  title={`Down: ${summary.down}`}
                />
              )}
              {summary.noMetrics > 0 && (
                <div
                  className="bg-gray-300 dark:bg-gray-600"
                  style={{ width: `${(summary.noMetrics / summary.total) * 100}%` }}
                  title={`No Agent: ${summary.noMetrics}`}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Two-Column Layout: Connected Clients + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Left: Connected Clients */}
        <Card
          title="Connected Clients"
          action={
            <Link
              to="/clients"
              className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
            >
              View All
            </Link>
          }
        >
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {connectedClients.length > 0 ? (
              connectedClients.map((client: Client) => {
                const health = computeHealth(client);
                return (
                  <Link
                    key={client._id}
                    to={`/clients/${client.client_id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span
                      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${healthColors[health]} ${health === 'healthy' ? 'animate-pulse' : ''}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {client.deployment_name || client.client_name || client.client_id}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                        {client.hostname || client.local_ip || client.client_id}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {client.metrics && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                          CPU {Math.round(client.metrics.cpu_percent || 0)}%
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {formatTimeAgo(client.last_seen)}
                      </span>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <Wifi size={24} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400">No connected clients</p>
              </div>
            )}
          </div>
          {connectedClients.length > 0 && stats.connected > 10 && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-center">
              <Link
                to="/clients?status=connected"
                className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
              >
                +{stats.connected - 10} more clients
              </Link>
            </div>
          )}
        </Card>

        {/* Right: Recent Activity */}
        <Card
          title="Recent Activity"
          action={
            <Link
              to="/logs"
              className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
            >
              View All
            </Link>
          }
        >
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {logs.length > 0 ? (
              logs.map(log => (
                <div key={log._id} className="flex items-start gap-3 px-4 py-2.5">
                  <div className="mt-0.5 flex-shrink-0">
                    <Activity size={12} className="text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge variant={eventBadgeVariants[log.event_type] || 'gray'}>
                        {log.event_type.replace('_', ' ')}
                      </Badge>
                      {log.client_name && (
                        <Link
                          to={`/clients/${log.client_id}`}
                          className="text-[10px] text-purple-600 dark:text-purple-400 hover:underline truncate"
                        >
                          {log.client_name}
                        </Link>
                      )}
                    </div>
                    {log.details && (
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{log.details}</p>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                    {formatTimeAgo(log.timestamp)}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <Clock size={24} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400">No recent activity</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* All Clients Table */}
      <Card
        title="All Clients"
        action={
          <Link
            to="/clients"
            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
          >
            View All
          </Link>
        }
      >
        <Table>
          <Thead>
            <tr>
              <Th>Health</Th>
              <Th>Status</Th>
              <Th>Name</Th>
              <Th>Hostname</Th>
              <Th>IP</Th>
              <Th>CPU</Th>
              <Th>Memory</Th>
              <Th>Disk</Th>
              <Th>Last Seen</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {allClients.slice(0, 50).map((client: Client) => {
              const health = computeHealth(client);
              return (
                <Tr key={client._id} onClick={() => window.location.href = `/clients/${client.client_id}`}>
                  <Td>
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${healthColors[health]} ${health === 'healthy' ? 'animate-pulse-dot' : ''}`}
                      title={health}
                    />
                  </Td>
                  <Td><StatusBadge status={client.status} /></Td>
                  <Td>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {client.deployment_name || client.client_name || client.client_id}
                      </span>
                      {client.description && (
                        <p className="text-[10px] text-gray-400">{client.description}</p>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span className="text-gray-500 dark:text-gray-400">{client.hostname || 'N/A'}</span>
                  </Td>
                  <Td>
                    <span className="text-gray-500 dark:text-gray-400">{client.local_ip || 'N/A'}</span>
                  </Td>
                  <Td><MetricBar value={client.metrics?.cpu_percent} color="blue" /></Td>
                  <Td><MetricBar value={client.metrics?.memory_percent} color="indigo" /></Td>
                  <Td><MetricBar value={client.metrics?.disk_percent} color="purple" /></Td>
                  <Td>
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatTimeAgo(client.last_seen)}
                    </span>
                  </Td>
                  <Td>
                    <Link
                      to={`/clients/${client.client_id}`}
                      className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </Td>
                </Tr>
              );
            })}
            {allClients.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  No clients registered yet
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {allClients.length > 50 && (
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-center">
            <Link
              to="/clients"
              className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 text-xs font-medium"
            >
              View all {allClients.length} clients
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
